import {SelectionMode} from '@fluentui/react';
import React, {useMemo} from 'react';
import {GetVehicleProjectDetailsQuery} from '../hooks';
import {useNavigate, useParams} from 'react-router-dom';
import {ProjectDetailsPageParams} from '../vehicleProjectPagePaths';
import {
	useGetVehicleProjectParagraphsInfinityLazyQuery,
	useGetVehicleProjectParagraphsNextLazyQuery,
	useGetVehicleProjectParagraphsSortOrderLazyQuery,
} from '../hooks/AssignedParagraphsInfinity.generated';
import {
	InfinityList,
	InfinityListFilter,
	InfinityListView,
} from 'components/InfinityList';
import {ParagraphFilterOptions, RegulatoryDocumentParagraph} from 'types';
import {
	filters2MongoQuerySTD,
	useInfinityListStandardHooks,
} from 'components/InfinityList/InfinitListStandardHooks';
import {
	convertEntity2ParagraphId,
	convertParagraph2EntityId,
	useAssignedParagraphFilterOptions,
	useAssignedParagraphsColumn,
	useAssignedParagraphsColumnMetaData,
} from './AssignedParagraphsColumns';

const initialSortField = 'name';
const initialSortOrder = 'ASC';
const intBatchSize = 50;

export type VehicleProjectDetailsTabProps = {
	dataVehPro: GetVehicleProjectDetailsQuery | undefined;
	hidden: boolean;
};

type QueryDocumentsDBArg = {
	query: string;
	queryRegdoc: string;
};

const VehicleProjectDetailsTabParagraphs: React.FC<
	VehicleProjectDetailsTabProps
> = ({hidden}) => {
	const {modelSeries, generation} = useParams() as ProjectDetailsPageParams;

	const [getSortOrder] = useGetVehicleProjectParagraphsSortOrderLazyQuery();

	const [getAssignedParagraphsQuery, {loading: loadingQuery, data: dataQuery}] =
		useGetVehicleProjectParagraphsInfinityLazyQuery();

	const [getParagraphsNext, {loading: loadingDocuments}] =
		useGetVehicleProjectParagraphsNextLazyQuery();

	const navigate = useNavigate();

	const views: InfinityListView[] = useMemo(() => [], []);

	const initialView = 'STD';

	const arrIds = useMemo(
		() =>
			convertParagraph2EntityId(
				dataQuery?.vehicleProjectMasterParagraphsQuery.filteredParagraphsIds ??
					[],
			),
		[dataQuery],
	);
	const queryDocumentsDB = async (
		query: QueryDocumentsDBArg,
		sortField: string,
		sortOrder: string,
	) => {
		const {data: dataQuery} = await getAssignedParagraphsQuery({
			variables: {
				queryArgAssignedParagraphs: {
					modelSeries,
					generation,
					queryRegdoc: query.queryRegdoc ?? '{}',
					queryArgs: {query: query.query ?? '{}', sortField, sortOrder},
				},
			},
		});
		const arrDocumentNext = (
			dataQuery?.vehicleProjectMasterParagraphsQuery?.paragraphsBatch ?? []
		).map(x => ({
			...x,
			markets: x.parent.markets,
			mainKeywords: x.parent.mainKeywords,
			status: x.parent.status,
		}));

		const arrIDsNew = convertParagraph2EntityId(
			dataQuery?.vehicleProjectMasterParagraphsQuery?.filteredParagraphsIds ??
				[],
		);
		return {arrDocumentNext, arrIDsNew};
	};

	const getDocumentsNextDB = async (preFilteredIds: string[]) => {
		const {data: dataDocumentsNext} = await getParagraphsNext({
			variables: {
				nextParaArgs: {
					preFilteredParagraphIds: convertEntity2ParagraphId(preFilteredIds),
				},
			},
		});

		return dataDocumentsNext?.paragraphsNext ?? [];
	};

	const getSortOrderDB = async (
		preFilteredIds: string[],
		sortField: string,
	) => {
		const {data: dataSortOrder} = await getSortOrder({
			variables: {
				sortParaArgs: {
					sortField,
					preFilteredParagraphIds: convertEntity2ParagraphId(preFilteredIds),
				},
			},
		});

		return convertParagraph2EntityId(dataSortOrder?.paragraphsSortOrder ?? []);
	};

	const columns = useAssignedParagraphsColumn();

	const columnMetaData = useAssignedParagraphsColumnMetaData();

	const columnFilterOptions = useAssignedParagraphFilterOptions(
		dataQuery?.vehicleProjectMasterParagraphsQuery
			.paragraphFilterOptions as ParagraphFilterOptions,
	);

	const filters2MongoQuery = (allFilters: InfinityListFilter[]) => {
		const regDocFields = ['markets', 'mainKeywords', 'status'];
		const filters2MongoRegDoc = filters2MongoQuerySTD(
			columns.filter(x => regDocFields.includes(x.key)),
			columnMetaData.filter(x => regDocFields.includes(x.key)),
		);
		const filters2MongoParagraph = filters2MongoQuerySTD(
			columns.filter(x => !regDocFields.includes(x.key)),
			columnMetaData.filter(x => !regDocFields.includes(x.key)),
		);
		const query = filters2MongoRegDoc(allFilters);
		const queryRegdoc = filters2MongoParagraph(allFilters);
		return {query, queryRegdoc};
	};

	const combineViewAndUserFilterQuery = (
		queryUser: QueryDocumentsDBArg,
		views: InfinityListView[],
		viewKey?: string,
	) => {
		return queryUser;
	};

	const {
		arrDocument,
		keyPrefix,
		linksFilters,
		firstRenderSTD,
		applyFilterSTD,
		applySortSTD,
		loadItemsNextSTD,
	} = useInfinityListStandardHooks({
		views,
		intBatchSize,
		initialSortField,
		initialSortOrder,
		initialView,
		arrIds,
		generateLink: false,
		columns,
		columnMetaData,
		filters2MongoQuery,
		combineViewAndUserFilterQuery,
		queryDocumentsDB,
		getDocumentsNextDB,
		getSortOrderDB,
	});

	const handleViewingItem = (item: RegulatoryDocumentParagraph) => {
		navigate({
			pathname: `/requirements/${item.id}`,
		});
	};

	const numberOfElements = useMemo(
		() => arrIds.length,
		[dataQuery, arrDocument],
	);

	const createHref = (item: unknown) => `/requirements/`;

	return (
		<>
			{!hidden && (
				<InfinityList
					pageName={''}
					items={arrDocument}
					sticky={true}
					columns={columns}
					columnMetaData={columnMetaData}
					columnFilterOptions={columnFilterOptions}
					selectionMode={SelectionMode.none}
					stillLoading={loadingQuery || loadingDocuments}
					initalFilters={linksFilters}
					initialSortField={initialSortField}
					initialSortOrder={initialSortOrder}
					initialView={initialView}
					keyPrefix={keyPrefix}
					firstRender={firstRenderSTD}
					loadItemsNext={loadItemsNextSTD}
					applyFilter={applyFilterSTD}
					applySort={applySortSTD}
					handleInvokeItem={handleViewingItem}
					createHref={createHref}
					numberOfElements={numberOfElements}
					showHeader={false}
					views={views}
					advancedFilterShow={false}
				/>
			)}
		</>
	);
};

export default VehicleProjectDetailsTabParagraphs;
